import { MasterDataBuilderDto } from 'src/models/systemconfig';
import { ColumnWithType, DataGridAction, DataGridColumn, LookupDetails } from './datagrid-types';
import { Dispatch, SetStateAction } from 'react';
import {
  GridRowsProp,
  GridRowParams,
  MuiEvent,
  GridEventListener,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRenderCellParams
} from '@mui/x-data-grid-pro';

export const hasDisplayOrder = (columns: any[], toCamelCase?: (str: string | undefined) => string): boolean => {
  for (let columnsIndex = 0; columnsIndex < columns.length; columnsIndex++) {
    const element = columns[columnsIndex];

    if (toCamelCase?.(element.field) === 'displayOrder' || toCamelCase?.(element.name) === 'displayOrder') {
      return true;
    }
  }

  return false;
};

export const hasCode = (columns: any[], toCamelCase?: (str: string | undefined) => string): boolean => {
  for (let columnsIndex = 0; columnsIndex < columns.length; columnsIndex++) {
    const element = columns[columnsIndex];

    if (toCamelCase?.(element.field) === 'code' || toCamelCase?.(element.name) === 'code') {
      return true;
    }
  }

  return false;
};

export const lowerCaseRowHeaders = (rowHeaders: any[]): any[] => {
  const newHeaders: any[] = [];

  for (let index = 0; index < rowHeaders.length; index++) {
    if (rowHeaders[index].charAt(0) == rowHeaders[index].charAt(0).toLowerCase()) {
      newHeaders.push(rowHeaders[index]);
    } else {
      newHeaders.push(rowHeaders[index].charAt(0).toLowerCase() + rowHeaders[index].slice(1));
    }
  }

  return newHeaders;
};

export const getColsWithTypes = (rowHeaders: any[], masterDataObject?: MasterDataBuilderDto): ColumnWithType[] => {
  const headers = lowerCaseRowHeaders(rowHeaders);
  const colsWithTypes: ColumnWithType[] = [];

  const masterDataColumns = masterDataObject?.masterDataColumns;
  const dtoProperties = masterDataObject?.dtoProperties;
  const propertyTypes = masterDataObject?.propertyTypes;

  if (masterDataColumns) {
    for (let index = 0; index < masterDataColumns.length; index++) {
      const name = masterDataColumns[index].name;
      if (name == 'Id') {
        continue;
      }
      if (headers.includes(name != undefined ? name.charAt(0).toLowerCase() + name.slice(1) : '')) {
        const masterDataColumnDto = masterDataColumns[index];
        const dtoProperty = dtoProperties?.find(x => x.id == masterDataColumnDto?.dtoPropertyId);
        const propertyType = propertyTypes?.find(x => x.id == dtoProperty?.propertyTypeId);

        colsWithTypes.push({
          column: name != undefined ? name.charAt(0).toLowerCase() + name.slice(1) : '',
          type: propertyType?.code != undefined ? propertyType?.code : ''
        });
      }
    }
  }

  return colsWithTypes;
};

export function checkRowsForErrors(
  rows: any[],
  toCamelCase: (str: string | undefined) => string,
  lookupColumns: LookupDetails[] | undefined,
  t: (text?: string | undefined) => string,
  masterDataObject: MasterDataBuilderDto | undefined
) {
  const newRows: any[] = [];
  const rowCodes: any[] = [];
  const rowHeaders = lowerCaseRowHeaders(Object.keys(rows[0]));
  const colsWithTypes = getColsWithTypes(rowHeaders, masterDataObject);

  // ADD ERROR LIST PROP TO EACH ROW
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    row['errorList'] = [];
  }

  // ADD CODE VALUE TO ARRAY TO CHECK FOR DUPLICATES LATER
  if (rowHeaders.includes('code')) {
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      rowCodes.push(row['code']);
    }
  }

  for (let rowsIndex = 0; rowsIndex < rows.length; rowsIndex++) {
    const row = rows[rowsIndex];

    // RESET HAS ERROR PROP
    row['hasError'] = false;

    for (let rowHeadersIndex = 0; rowHeadersIndex < rowHeaders.length; rowHeadersIndex++) {
      if (
        rowHeaders[rowHeadersIndex] == 'id' ||
        rowHeaders[rowHeadersIndex] == 'importOrUpdate' ||
        rowHeaders[rowHeadersIndex] == 'hasError' ||
        rowHeaders[rowHeadersIndex] == 'errorList' ||
        rowHeaders[rowHeadersIndex] == 'importRow'
      ) {
        continue;
      }

      const colType = colsWithTypes.find(x => x.column == rowHeaders[rowHeadersIndex]);

      if (colType) {
        switch (colType.type) {
          case 'int':
            if (isNaN(row[colType.column])) {
              row[colType.column] = 0;

              continue;
            }
            break;

          case 'string':
            if (colType.column == 'code') {
              const anyDuplicates = rowCodes.filter((val, index) => index !== rowCodes.indexOf(val));
              if (anyDuplicates.length != 0 && anyDuplicates.includes(row[colType.column])) {
                const error = {
                  field: 'code',
                  errorMessage: t('Duplicate code')
                };
                row['hasError'] = true;
                row['errorList'].push(error);
              }
            }
            break;

          case 'bool':
            if (row[colType.column] == 'FALSE' || row[colType.column] == 'no' || row[colType.column] == false) {
              row[colType.column] = false;

              continue;
            }
            if (row[colType.column] == 'TRUE' || row[colType.column] == 'yes' || row[colType.column] == true) {
              row[colType.column] = true;

              continue;
            }
            row[colType.column] = false;

            break;

          case 'uniqueidentifier':
            if (lookupColumns) {
              const lookup = lookupColumns.find(x => x.lookupColumn == colType.column);
              const lookupDisplayProperty = lookup?.lookupInfo.lookupDtoProperties?.find(
                x => x.id == lookup.lookupInfo.lookupDisplayPropertyId
              )?.name;

              if (row[colType.column] == '') {
                continue;
              }

              const lookupRow = lookup?.lookupRows?.find(
                x => x[toCamelCase(lookupDisplayProperty)] == row[colType.column]
              );

              if (!lookupRow) {
                // has it been mapped after fixing error?
                const checkAgain = lookup?.lookupRows?.find(x => x.id == row[colType.column]);

                if (!checkAgain) {
                  row['hasError'] = true;
                  row['errorList'].push({
                    field: colType.column,
                    errorMessage: t('Error mapping value')
                  });
                  row[colType.column] = '';

                  continue;
                } else {
                  row[colType.column] = checkAgain[toCamelCase(lookupDisplayProperty)];
                }
              } else {
                row[colType.column] = lookupRow[toCamelCase(lookupDisplayProperty)];
              }
            }
            break;

          default:
            break;
        }
      }
    }

    newRows.push(row);
  }

  return newRows;
}

export function generateModel(
  popupDisplayHeaders: any[],
  toCamelCase: (str: string | undefined) => string,
  lookupData: LookupDetails[],
  row: any,
  id?: string,
  version?: string,
  masterDataObject?: MasterDataBuilderDto
): any {
  const newModel: any = row;
  const columns = masterDataObject?.masterDataColumns;
  const dtoProperties = masterDataObject?.dtoProperties;
  const propertyTypes = masterDataObject?.propertyTypes;
  const colsWithTypes: ColumnWithType[] = [];

  // set version if updating row
  if (version) {
    newModel.version = version;
  }

  // empty out unnecessary columns
  newModel.importOrUpdate = undefined;
  newModel.id = undefined;
  newModel.hasError = undefined;
  newModel.importRow = undefined;
  newModel.errorList = undefined;

  // get column names and types for model
  for (let index = 0; index < popupDisplayHeaders.length; index++) {
    if (popupDisplayHeaders[index] == 'importOrUpdate' || popupDisplayHeaders[index] == 'importRow') {
      continue;
    }

    const column: string = popupDisplayHeaders[index];
    const masterDataColumnDto = columns?.find(x => toCamelCase(x.name) == column);
    const dtoProperty = dtoProperties?.find(x => x.id == masterDataColumnDto?.dtoPropertyId);
    const propertyType = propertyTypes?.find(x => x.id == dtoProperty?.propertyTypeId);
    colsWithTypes.push({
      column: column.charAt(0).toLowerCase() + column.slice(1),
      type: propertyType!.code ? propertyType!.code : ''
    });
  }

  // map model props from CSV value
  for (let index = 0; index < colsWithTypes.length; index++) {
    const valueFromCSV = newModel[colsWithTypes[index].column];

    switch (colsWithTypes[index].type) {
      case 'int':
        newModel[colsWithTypes[index].column] = parseInt(valueFromCSV);
        break;

      case 'bool':
        if (valueFromCSV == 'no') {
          newModel[colsWithTypes[index].column] = false;
        }
        if (valueFromCSV == 'yes') {
          newModel[colsWithTypes[index].column] = true;
        }
        if (valueFromCSV == 'true') {
          newModel[colsWithTypes[index].column] = true;
        }
        if (valueFromCSV == 'false') {
          newModel[colsWithTypes[index].column] = false;
        }
        break;

      case 'uniqueidentifier': {
        // set model lookups to guids
        const lookup = lookupData.find(x => x.lookupColumn == colsWithTypes[index].column);
        const lookupDisplayProperty = lookup?.lookupInfo.lookupDtoProperties?.find(
          x => x.id == lookup.lookupInfo.lookupDisplayPropertyId
        )?.name;
        const lookupRow = lookup?.lookupRows?.find(
          x => x[lookupDisplayProperty!.charAt(0).toLowerCase() + lookupDisplayProperty!.slice(1)] == valueFromCSV
        );
        if (lookupRow == undefined) {
          newModel[colsWithTypes[index].column] = '';
          break;
        }
        newModel[colsWithTypes[index].column] = lookupRow.id;
        break;
      }
    }

    // set model remarks
    if (newModel.remarks == '' && id == undefined) {
      newModel.remarks = 'Imported from CSV';
    }
    if (newModel.remarks == '' && id != undefined) {
      newModel.remarks = 'Updated from CSV';
    }
  }

  return newModel;
}

export function convertToDataTableType(value: any): DataGridColumn['type'] {
  switch (value) {
    case 'int':
      return 'number';
    case 'string':
      return 'string';
    case 'uniqueidentifier':
      return 'string';
    case 'bool':
      return 'boolean';
    case 'datetimeoffset':
      return 'dateTime';
    default:
      return value.toString();
  }
}

export const mapEditableRows = (
  dataSource: any[],
  setRowsFromCSV?: Dispatch<SetStateAction<any[]>>,
  fullDataSource?: any[],
  setEditableGridRows?: Dispatch<SetStateAction<GridRowsProp>>
) => {
  const editableDataGridRows: any[] = [];
  for (let index = 0; index < dataSource.length; index++) {
    dataSource[index].id = String(index);
    editableDataGridRows.push(dataSource[index]);
  }
  setRowsFromCSV?.(fullDataSource ?? []);
  setEditableGridRows?.(editableDataGridRows);
};

export const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
  event.defaultMuiPrevented = true;
};

export const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
  event.defaultMuiPrevented = true;
};

export const handleEditClick =
  (id: GridRowId, setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>, rowModesModel: GridRowModesModel) =>
  () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

export const handleSaveClick =
  (id: GridRowId, setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>, rowModesModel: GridRowModesModel) =>
  () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

export const handleCancelClick =
  (id: GridRowId, setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>, rowModesModel: GridRowModesModel) =>
  () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });
  };

export const handleImportRowChange = (
  params: GridRenderCellParams<any, any, any>,
  checked: boolean,
  dataSource: any[],
  fullDataSource?: any[],
  setRowsFromCSV?: Dispatch<SetStateAction<any[]>>,
  setRowsToCheck?: Dispatch<SetStateAction<any[] | undefined>>
) => {
  const rowToUpdate = dataSource.findIndex(x => x.id == params.row.id);
  dataSource[rowToUpdate].importRow = checked;

  if (fullDataSource) {
    fullDataSource[rowToUpdate].importRow = checked;
  }

  setRowsFromCSV?.(fullDataSource ?? []);
  setRowsToCheck?.(dataSource);
};

export const buildNonEditableCustomActions = (customActions: DataGridAction[]) => {
  return [...customActions];
};

export const handleChipText = (column: DataGridColumn, value: any) => {
  return column.chipOptions?.renderCellText ? column.chipOptions?.renderCellText?.(value) : column.chipOptions?.label;
};

export const handleChipColor = (col: DataGridColumn, row: { [x: string]: any }) => {
  return col.chipOptions?.color?.(row[col.field]);
};

export const handleCustomChipColor = (col: DataGridColumn, row: { [x: string]: any }) => {
  return col.chipOptions?.customColorOverride?.(row[col.field]);
};

export const escapeODataLiteral = (value: string): string => value.replaceAll("'", "''");
